import {
  getCountryCodeByName,
  salesforceCountries,
} from "@karbonhq/marketing-shared-utils/utils/countries"
import { track } from "@src/utils/analytics"
import { navigate } from "gatsby"
import PropTypes from "prop-types"
import React, { useContext } from "react"
import styled, { css } from "styled-components"
import breakpoint from "styled-components-breakpoint"
import { Margin, mb } from "styled-components-spacing/dist/cjs"

import categoriesIcon from "../images/categories-menu-icon.svg"
import kpiIcon from "../images/kpi-icon.svg"
import listIcon from "../images/list-menu-icon.svg"
import starIcon from "../images/star-menu-icon.svg"
import { Select } from "./atoms/FormFields"
import { Link } from "./atoms/Link"
import Logo from "./atoms/Logo"
import { All, ROW, useAllTemplateRegions } from "./hooks/useAllTemplateRegions"
import { useAllTemplateTypes } from "./hooks/useAllTemplateTypes"
import { VisitorLocaleContext } from "./providers/VisitorLocaleProvider"
import { SearchInput } from "./search/Elements"
import { HR } from "./Typography"

const LinkResolver = require("../utils/linkResolver")

export const SIDEBAR_BREAKPOINT = "lg"

const SidebarContainer = styled.div`
  position: fixed;
  display: ${props => (props.menuOpen ? `flex` : `none`)};
  background: ${props => props.theme.colors.ash};
  flex-direction: column;
  top: ${props => props.theme.menuHeight}px;
  bottom: 0;
  right: 0;
  left: 0;
  padding: 0 16px 16px;
  overflow-y: auto;
  border-right: 1px solid ${props => props.theme.colors.lightGrey};
  font-size: 16px;
  z-index: 1;

  ${breakpoint(SIDEBAR_BREAKPOINT)(
    () => css`
      padding: 0 8px 8px;
      /* to fill in the background where padding of 20px is */
      margin-left: -20px;
      top: 0;
      width: ${props => props.theme.sideBarWidth}px;
      right: auto;
      left: auto;
      display: flex;
    `
  )}

  nav {
    flex: 1;
    overflow-y: auto;
    min-height: 150px;
    ${mb(2)};
  }
`

const SideBarTitle = styled.h4`
  font-weight: 600;
  font-size: 16px;
  line-height: 20px;
  margin: 0;
  padding: 5px 0;
`

const SideBarLink = styled(props => {
  const clicked = e => {
    e.preventDefault()
    navigate(props.to)
    props.setMenuOpen(false)

    track(`WE-TemplateMenuClicked`, {
      label: props.children.toString(),
    })
  }
  return (
    <Link
      onClick={clicked}
      activeClassName={props.activeClassName}
      className={props.className}
      to={props.to}
    >
      {props.children}
    </Link>
  )
})`
  display: block;
  color: black;
  font-weight: 400;
  padding: 8px 0;
  border-radius: 4px;
  text-decoration: none;
  ${props =>
    props.indented &&
    css`
      padding-left: 52px;
    `};
  ${props =>
    props.icon &&
    css`
      background-image: url(${props.icon});
      background-repeat: no-repeat;
      background-position: 12px center;
      background-size: 24px;
      padding-left: 48px;
      font-weight: 600;
    `};
  ${props =>
    props.icon === kpiIcon &&
    css`
      background-size: 22px;
    `};

  &:hover {
    background-color: white;
    opacity: 0.5;
  }

  &.active {
    background-color: white;
  }
`

const SmallLink = styled(Link)`
  font-size: 16px;
  color: black;
  display: block;
`

function RegionSelector() {
  const allRegions = useAllTemplateRegions()
  const { regionFilter, setRegionFilter } = useContext(VisitorLocaleContext)

  const onRegionChange = e => {
    const regionName = e.target.value
    let countryCode

    if (regionName === All) {
      countryCode = "All"
    } else if (regionName === ROW) {
      countryCode = "ROW"
    } else {
      countryCode = getCountryCodeByName(regionName)
    }

    setRegionFilter(countryCode)
  }
  const selectValue = region => {
    if (region === "ROW") return ROW
    if (region === "All") return All
    return salesforceCountries[region] || All
  }

  return (
    <Select
      id="regionFilter"
      value={selectValue(regionFilter)}
      small
      onChange={onRegionChange}
      css={css`
        background-color: transparent;
        border: none;
        width: 130px;
        padding: 4px 0 0;
        margin: 0;
      `}
    >
      {allRegions.map(region => (
        <option value={region} key={region}>
          {region}
        </option>
      ))}
    </Select>
  )
}

export const SideBar = props => {
  const allTypes = useAllTemplateTypes()
  const toggleCookieDialog = () => {
    if (window?.Osano?.cm) {
      window.Osano.cm.showDrawer()
    }
  }

  return (
    // we need to wrap in a basic div for
    <SidebarContainer menuOpen={props.menuOpen} className={props.className}>
      <Margin
        bottom={2}
        top={2}
        css={breakpoint(
          `xs`,
          SIDEBAR_BREAKPOINT
        )(
          () =>
            css`
              display: none;
            `
        )}
      >
        <Logo />
      </Margin>
      <Margin
        bottom={2}
        css={breakpoint(
          `xs`,
          SIDEBAR_BREAKPOINT
        )(
          () =>
            css`
              display: none;
            `
        )}
      >
        <SearchInput className="searchInput" />
      </Margin>

      <nav>
        <SideBarTitle
          as={SideBarLink}
          to={"/"}
          setMenuOpen={props.setMenuOpen}
          icon={starIcon}
          activeClassName="active"
          css={css`
            margin-top: 4px;
            ${breakpoint(SIDEBAR_BREAKPOINT)(
              css`
                margin-top: 0;
              `
            )}
          `}
        >
          Discover
        </SideBarTitle>
        <SideBarTitle
          as={SideBarLink}
          to={"/all"}
          setMenuOpen={props.setMenuOpen}
          icon={listIcon}
          activeClassName="active"
        >
          All Templates
        </SideBarTitle>
        <SideBarTitle
          css={css`
            background-image: url(${categoriesIcon});
            background-repeat: no-repeat;
            background-position: 12px center;
            background-size: 24px;
            padding: 12px 0 12px 48px;
          `}
        >
          Categories
        </SideBarTitle>
        {allTypes.map(type => (
          <SideBarLink
            indented
            activeClassName="active"
            to={LinkResolver("TemplateList", type)}
            key={type}
            setMenuOpen={props.setMenuOpen}
          >
            {type}
          </SideBarLink>
        ))}

        <SideBarTitle
          as={SideBarLink}
          to={"/kpi-dashboards"}
          setMenuOpen={props.setMenuOpen}
          icon={kpiIcon}
          activeClassName="active"
        >
          KPI Dashboards
        </SideBarTitle>
      </nav>
      <div
        className="sideBarPadding"
        css={css`
          padding-left: 16px;
        `}
      >
        {!props.hideRegionSelector && (
          <>
            <label
              htmlFor="regionFilter"
              css={css`
                display: block;
                font-weight: bold;
                font-size: 16px;
              `}
            >
              Region
            </label>
            <RegionSelector />
          </>
        )}

        <HR mt={2} mb={2} />

        <SmallLink href="https://karbonhq.com">Discover Karbon</SmallLink>
        <SmallLink href="https://karbonhq.com/resources">Magazine</SmallLink>
        <SmallLink href="https://karbonhq.com/services">Services</SmallLink>
        <SmallLink onClick={toggleCookieDialog} role="button">
          Cookie Settings
        </SmallLink>
      </div>
    </SidebarContainer>
  )
}

SideBar.propTypes = {
  hideRegionSelector: PropTypes.bool,
  menuOpen: PropTypes.bool.isRequired,
  setMenuOpen: PropTypes.func.isRequired,
}
